import { motion } from "framer-motion";
import { IMAGES } from "../data";

const CHAPTERS = [
  {
    n: "01",
    title: "Beyond the Boardroom",
    body: "Real transformation doesn't happen under fluorescent lights. It happens when a team is mid-river, mid-battle, mid-build — when titles dissolve and instinct takes over.",
  },
  {
    n: "02",
    title: "Nature is the Venue",
    body: "The Barn House sits on a living 4-acre rubber plantation in Kiriberiya, Panadura. Over 1,000 trees preserved, 90% upcycled architecture — the island's most awarded sustainable playground.",
  },
  {
    n: "03",
    title: "Adventure with Purpose",
    body: "Every raft, rope and blaster is a designed learning moment. Our programmes ride on the LEAP competency framework — assess, design, experience, debrief, transform.",
  },
  {
    n: "04",
    title: "Teams Become Legends",
    body: "From corporate squads to families and friend circles — people arrive as groups and leave as stories worth retelling.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Manifesto = () => (
  <section data-testid="manifesto-section" className="relative bg-forest py-28 md:py-44">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="mb-20 md:mb-32"
      >
        <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="manifesto-label">
          The Manifesto
        </p>
        <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream max-w-3xl leading-[1.05]">
          Where the wild builds <span className="italic text-amber">what work cannot.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-10">
        <div className="lg:col-span-7 space-y-0">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              data-testid={`manifesto-chapter-${c.n}`}
              className={`group border-t border-cream/10 py-12 md:py-16 grid grid-cols-12 gap-6 ${
                i % 2 === 1 ? "md:pl-16" : ""
              }`}
            >
              <span className="col-span-3 md:col-span-2 font-display text-5xl md:text-6xl text-stroke-amber group-hover:text-amber transition-colors duration-500">
                {c.n}
              </span>
              <div className="col-span-9 md:col-span-10">
                <h3 className="font-display text-2xl md:text-4xl text-cream mb-4 tracking-tight">
                  {c.title}
                </h3>
                <p className="text-base md:text-lg leading-relaxed text-cream/70 max-w-xl">{c.body}</p>
              </div>
            </motion.div>
          ))}
          <div className="border-t border-cream/10" />
        </div>

        <div className="lg:col-span-5 space-y-10">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="group relative overflow-hidden clip-frame border border-cream/10"
          >
            <img
              src={IMAGES.barnSession}
              alt="Team strategy session inside the wooden Barn hall"
              className="w-full h-[420px] object-cover image-zoom"
              data-testid="manifesto-image-barn"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-night/90 to-transparent p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-amber">Inside the Barn</p>
            </div>
          </motion.div>
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="group relative overflow-hidden clip-frame border border-cream/10 lg:ml-16"
          >
            <img
              src={IMAGES.raftingReal}
              alt="Rafting team paddling the estate river"
              className="w-full h-[340px] object-cover image-zoom"
              data-testid="manifesto-image-rafting"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-night/90 to-transparent p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-amber">The River Run</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
